import React, { useEffect, useState, useCallback, useRef } from 'react';
import api from '../services/api';
import Modal from './Modal';
import Spinner from './Spinner';
import { toast } from './Toast';
import { exportCSV, printTable } from './ExportButton';
import EditableSelect from './EditableSelect';
import { createWorker } from 'tesseract.js';
import { parseazaBonCombustibil } from '../utils/ocrBon';

const TIPURI = ['Motorina','Benzina','GPL','AdBlue','KW'];

// Unitatea afisata depinde de tip: KW (electric) se masoara in kWh, restul in litri.
const unitatePentru = (tip) => tip === 'KW' ? 'kWh' : 'l';

const EMPTY_FACT = {
  furnizor:'', nrFactura:'', dataFactura:'', tipCombustibil:'',
  cantitate:'', pretUnitar:'', nrInmatriculare:'', kmLaAlimentare:'',
  bonFiscal: true
};
const EMPTY_CONS = {
  nrInmatriculare:'', tipCombustibil:'', cantitate:'',
  kmLaAlimentare:'', dataAlimentarii:''
};

function fmt(val) {
  const n = parseFloat(val || 0);
  return n.toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtKm(val) {
  if (val === null || val === undefined || val === '') return '';
  const n = parseInt(val, 10);
  if (isNaN(n)) return val;
  return n.toLocaleString('ro-RO');
}

function FilterInput({ value, onChange }) {
  return <input value={value} onChange={e => onChange(e.target.value)} placeholder="Filtrează..."
    style={{width:'100%',padding:'3px 6px',fontSize:11,border:'1px solid #ccc',borderRadius:3}}/>;
}

export default function CarburantTab() {
  const isDriver = (localStorage.getItem('rol') || '') === 'conducator_auto';
  const [tab, setTab] = useState('facturi');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sold, setSold] = useState([]);
  const [nruri, setNruri] = useState([]);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(EMPTY_FACT);
  const [filters, setFilters] = useState({});
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sort, setSort] = useState({ col:'dataFactura', dir:'desc' });
  const [selectedRow, setSelectedRow] = useState(null);
  const [furnizori, setFurnizori] = useState([]);
  const [soldInfo, setSoldInfo] = useState(null);
  const [combustibilAuto, setCombustibilAuto] = useState('');
  const username = localStorage.getItem('username') || '';

  // --- Scanare bon fiscal (OCR local, in browser, cu Tesseract.js) ---
  const [scanand, setScanand] = useState(false);
  const [progresScan, setProgresScan] = useState(0);
  const [campuriScanate, setCampuriScanate] = useState(new Set());
  // ID-ul randului din consumCarburant de evidentiat, dupa ce ai editat o factura
  // legata de el (vezi save() mai jos) - ramane setat pana editezi urmatoarea factura legata.
  const [randConsumEvidentiat, setRandConsumEvidentiat] = useState(null);
  const fisierScanRef = useRef(null);

  // --- Verificare in timp real daca bonul a mai fost inregistrat ---
  // Se declanseaza automat de fiecare data cand Furnizor + Nr. factura + Data sunt
  // toate completate si se schimba - fie din scanarea cu camera, fie din tastare
  // manuala. Debounce scurt, ca sa nu bombardam serverul la fiecare litera tastata.
  const [verificareDuplicat, setVerificareDuplicat] = useState({ stare: 'idle', rezultate: [] });

  const deschideScanare = () => fisierScanRef.current?.click();

  // Pregateste poza pentru OCR: redimensioneaza la o latime "optima" pentru citire
  // (nici prea mica - text ilizibil, nici prea mare - foarte lent fara beneficiu real),
  // trece pe alb-negru si intinde automat contrastul (histograma, cu 1% cutoff ca sa nu
  // fie deranjat de cateva pixeli extremi, ex. reflexii sau bare de cod de bare).
  // Bonurile fiscale (hartie termica, poze facute pe o masa/fundal) au nevoie de asta -
  // fara preprocesare, motorul OCR citeste in mare parte text ilizibil.
  const preproceseazaImagine = (fisier, latimeTinta = 1600) => new Promise((resolve, reject) => {
    const imagine = new Image();
    imagine.onload = () => {
      const scara = latimeTinta / imagine.width;
      const w = Math.max(1, Math.round(imagine.width * scara));
      const h = Math.max(1, Math.round(imagine.height * scara));

      const canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(imagine, 0, 0, w, h);

      const imgData = ctx.getImageData(0, 0, w, h);
      const d = imgData.data;

      const histograma = new Array(256).fill(0);
      const gri = new Uint8ClampedArray(w * h);
      for (let i = 0, p = 0; i < d.length; i += 4, p++) {
        const g = Math.round(0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2]);
        gri[p] = g;
        histograma[g]++;
      }

      const totalPixeli = w * h;
      const cutoff = totalPixeli * 0.01;
      let acumulat = 0, minP = 0, maxP = 255;
      for (let i = 0; i < 256; i++) { acumulat += histograma[i]; if (acumulat > cutoff) { minP = i; break; } }
      acumulat = 0;
      for (let i = 255; i >= 0; i--) { acumulat += histograma[i]; if (acumulat > cutoff) { maxP = i; break; } }
      const interval = Math.max(maxP - minP, 1);

      for (let i = 0, p = 0; i < d.length; i += 4, p++) {
        let val = ((gri[p] - minP) / interval) * 255;
        val = val < 0 ? 0 : (val > 255 ? 255 : val);
        d[i] = d[i + 1] = d[i + 2] = val;
      }
      ctx.putImageData(imgData, 0, 0);

      URL.revokeObjectURL(imagine.src);
      canvas.toBlob(blob => blob ? resolve(blob) : reject(new Error('Nu s-a putut procesa imaginea')), 'image/png');
    };
    imagine.onerror = () => reject(new Error('Nu s-a putut încărca imaginea'));
    imagine.src = URL.createObjectURL(fisier);
  });

  const scaneazaBon = async (e) => {
    const fisier = e.target.files?.[0];
    e.target.value = '';
    if (!fisier) return;

    setScanand(true);
    setProgresScan(0);
    let worker;
    try {
      // Fisierele Tesseract sunt auto-gazduite in /public/tesseract (worker, core WASM,
      // date lingvistice pentru romana) - NU se descarca de pe niciun CDN extern, ca sa
      // respecte politica stricta de securitate (CSP) a aplicatiei.
      worker = await createWorker('ron', 1, {
        workerPath: '/tesseract/worker.min.js',
        corePath: '/tesseract/core/',
        langPath: '/tesseract/lang-data/',
        // IMPORTANT: implicit, tesseract.js porneste worker-ul printr-un Blob URL, pe care
        // CSP-ul strict al aplicatiei (script-src 'self', fara blob:) il blocheaza silentios
        // - de-acolo eroarea "Undefined" din browser. Cu workerBlobURL:false, worker-ul
        // porneste direct din fisierul same-origin de mai sus, permis de CSP fara exceptii.
        workerBlobURL: false,
        logger: m => {
          if (m.status === 'recognizing text') setProgresScan(Math.round(m.progress * 100));
        },
      });
      const imaginePregatita = await preproceseazaImagine(fisier);
      const { data: { text } } = await worker.recognize(imaginePregatita);
      const campuri = parseazaBonCombustibil(text);

      const gasite = new Set(Object.entries(campuri).filter(([, v]) => v).map(([k]) => k));
      setCampuriScanate(gasite);
      setForm(prev => ({ ...prev, ...campuri }));

      if (gasite.size === 0) {
        toast('Nu am putut citi nimic clar de pe bon - completează manual câmpurile.', 'error');
      } else if (gasite.size < 6) {
        toast(`Am completat ${gasite.size} din 6 câmpuri - verifică și completează restul.`, 'error');
      } else {
        toast('Bon citit cu succes! Verifică datele înainte de a salva.');
      }
    } catch (err) {
      const mesaj = err?.message || err?.name || (typeof err === 'string' ? err : 'eroare necunoscută la inițializarea scanării');
      toast('Eroare la citirea bonului: ' + mesaj, 'error');
    } finally {
      if (worker) await worker.terminate();
      setScanand(false);
      setProgresScan(0);
    }
  };

  useEffect(() => {
    api.get('/api/auto/nruri').then(r => setNruri(r.data.map(x => x.nrInmatriculare)));
    api.get('/api/carburant/furnizori').then(r => setFurnizori(r.data.map(x => x.furnizor)));
  }, []);

  useEffect(() => { loadTab(tab); setFilters({}); setDateFrom(''); setDateTo(''); setSelectedRow(null); }, [tab]);

  const loadTab = (t) => {
    setLoading(true);
    if (t === 'facturi') api.get('/api/carburant/facturi').then(r => setRows(r.data)).finally(() => setLoading(false));
    else if (t === 'consum') api.get('/api/carburant/consum').then(r => setRows(r.data)).finally(() => setLoading(false));
    else api.get('/api/carburant/sold').then(r => setSold(r.data)).finally(() => setLoading(false));
  };

  const setFilter = (col, val) => setFilters(prev => ({ ...prev, [col]: val }));
  const toggleSort = (col) => setSort(prev => ({ col, dir: prev.col === col && prev.dir === 'asc' ? 'desc' : 'asc' }));
  const sortIcon = (col) => sort.col === col ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ' ⇅';

  // Filtrare col + data
  const filtered = rows.filter(r => {
    const dateCol = tab === 'facturi' ? r.dataFactura : r.dataAlimentarii;
    if (dateFrom && dateCol && dateCol < dateFrom) return false;
    if (dateTo && dateCol && dateCol > dateTo) return false;
    return Object.entries(filters).every(([col, val]) =>
      !val || String(r[col] || '').toLowerCase().includes(val.toLowerCase())
    );
  });

  const sorted = [...filtered].sort((a, b) => {
    const av = String(a[sort.col] || ''), bv = String(b[sort.col] || '');
    return sort.dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
  });

  // Totaluri dinamice pe filtrate
  const totalCantitate = filtered.reduce((s, r) => s + parseFloat(r.cantitate || 0), 0);
  const totalValoare = filtered.reduce((s, r) => s + (parseFloat(r.cantitate || 0) * parseFloat(r.pretUnitar || 0)), 0);

  // Consum mediu (Varianta 2) - are sens doar cand e filtrata o singura masina (altfel
  // s-ar amesteca km-ul unor vehicule diferite). Exclude ULTIMA alimentare din interval
  // (litrii ei acopera un drum care nu s-a intamplat inca - "inca in rezervor").
  let consumMediu = null;
  if (tab === 'consum' && filters.nrInmatriculare) {
    const dinInterval = [...filtered]
      .filter(r => r.kmLaAlimentare && !isNaN(parseFloat(r.kmLaAlimentare)))
      .sort((a, b) => String(a.dataAlimentarii).localeCompare(String(b.dataAlimentarii)));
    if (dinInterval.length >= 2) {
      const kmPrim = parseFloat(dinInterval[0].kmLaAlimentare);
      const kmUltim = parseFloat(dinInterval[dinInterval.length - 1].kmLaAlimentare);
      const litriFaraUltima = dinInterval.slice(0, -1).reduce((s, r) => s + parseFloat(r.cantitate || 0), 0);
      const distanta = kmUltim - kmPrim;
      if (distanta > 0) consumMediu = (litriFaraUltima / distanta) * 100;
    }
  }

  const onAutoSelect = useCallback(async (nr) => {
    if (!nr) { setSoldInfo(null); setCombustibilAuto(''); return; }
    try {
      const autoList = await api.get('/api/auto');
      const auto = autoList.data.find(a => a.nrInmatriculare === nr);
      if (auto?.combustibil) {
        setCombustibilAuto(auto.combustibil);
        setForm(prev => ({ ...prev, tipCombustibil: auto.combustibil }));
        const soldRes = await api.get(`/api/carburant/sold/${encodeURIComponent(auto.combustibil)}`);
        setSoldInfo(soldRes.data);
      }
    } catch (e) { setSoldInfo(null); }
  }, []);

  // Ruleaza automat cand Furnizor + Nr. factura + Data sunt toate completate (scanate
  // sau tastate manual) si tab-ul activ e "facturi" - avertizeaza daca bonul asta
  // exista deja in baza de date, INAINTE ca utilizatorul sa apese Salveaza.
  useEffect(() => {
    if (!modal || tab !== 'facturi') { setVerificareDuplicat({ stare: 'idle', rezultate: [] }); return; }
    const furnizor = (form.furnizor || '').trim();
    const nrFactura = (form.nrFactura || '').trim();
    const dataFactura = form.dataFactura;
    if (!furnizor || !nrFactura || !dataFactura) { setVerificareDuplicat({ stare: 'idle', rezultate: [] }); return; }

    setVerificareDuplicat({ stare: 'verifica', rezultate: [] });
    const timer = setTimeout(() => {
      api.get('/api/carburant/facturi/verifica-duplicat', { params: { furnizor, nrFactura, dataFactura } })
        .then(r => {
          // Cand editam o factura existenta, nu ne alarmam de propria ei inregistrare.
          const rezultate = (r.data || []).filter(x => !(modal.mode === 'edit' && x.nrCrt === modal.id));
          setVerificareDuplicat({ stare: rezultate.length > 0 ? 'duplicat' : 'ok', rezultate });
        })
        .catch(() => setVerificareDuplicat({ stare: 'idle', rezultate: [] }));
    }, 500);
    return () => clearTimeout(timer);
  }, [modal, tab, form.furnizor, form.nrFactura, form.dataFactura]);

  const openAdd = () => {
    // Daca exista o singura masina disponibila (flota cu un singur vehicul, sau
    // conducator auto cu o singura masina alocata), o completam automat.
    const nrAutoAuto = nruri.length === 1 ? nruri[0] : '';
    setForm(tab === 'facturi'
      ? { ...EMPTY_FACT, bonFiscal: true, nrInmatriculare: nrAutoAuto }
      : { ...EMPTY_CONS, nrInmatriculare: nrAutoAuto });
    setSoldInfo(null); setCombustibilAuto(''); setCampuriScanate(new Set());
    setModal({ mode: 'add' });
    if (nrAutoAuto) onAutoSelect(nrAutoAuto);
  };

  const openEdit = (row) => {
    setForm({ ...row, bonFiscal: false });
    setSoldInfo(null); setCombustibilAuto(''); setCampuriScanate(new Set());
    setModal({ mode: 'edit', id: tab === 'facturi' ? row.nrCrt : row.nrCrtCombust });
  };

  const save = async () => {
    try {
      let consumCarburantIdLegat = null;
      if (tab === 'facturi') {
        const payload = { ...form, operare: username };
        if (modal.mode === 'add') {
          await api.post('/api/carburant/facturi', payload);
        } else {
          const r = await api.put(`/api/carburant/facturi/${modal.id}`, payload);
          consumCarburantIdLegat = r.data?.consumCarburantId || null;
        }
      } else {
        const payload = { ...form, operare: username };
        if (modal.mode === 'add') await api.post('/api/carburant/consum', payload);
        else await api.put(`/api/carburant/consum/${modal.id}`, payload);
      }
      setModal(null); loadTab(tab);
      if (consumCarburantIdLegat) {
        setRandConsumEvidentiat(consumCarburantIdLegat);
        toast('Factură actualizată! Atenție: e legată de o alimentare din Consum/Alimentări — verifică și acolo (rândul e evidențiat).', 'warning');
      } else {
        toast(modal.mode === 'add' ? 'Înregistrare adăugată!' : 'Înregistrare actualizată!');
      }
    } catch (e) {
      toast('Eroare: ' + (e.response?.data?.error || e.message), 'error');
    }
  };

  const del = async (row) => {
    const id = tab === 'facturi' ? row.nrCrt : row.nrCrtCombust;
    const mesaj = tab === 'facturi'
      ? `Ștergi factura ${row.nrFactura} (${row.cantitate} l ${row.tipCombustibil}, ${row.dataFactura}) de la ${row.furnizor}?`
      : `Ștergi alimentarea de ${row.cantitate} l din ${row.dataAlimentarii} pentru ${row.nrInmatriculare}?`;
    if (!window.confirm(mesaj)) return;
    try {
      const ep = tab === 'facturi' ? '/api/carburant/facturi' : '/api/carburant/consum';
      await api.delete(`${ep}/${id}`);
      loadTab(tab); toast('Înregistrare ștearsă!');
    } catch (e) { toast('Eroare la ștergere', 'error'); }
  };

  const recalculeaza = async (nrAuto) => {
    if (!nrAuto) { toast('Selectează un vehicul (click pe un rând) pentru recalculare', 'error'); return; }
    if (!window.confirm(`Recalculezi tot istoricul de consum pentru ${nrAuto}, în ordine cronologică?`)) return;
    try {
      const r = await api.post(`/api/carburant/consum/recalculeaza/${encodeURIComponent(nrAuto)}`);
      toast(`Recalculat: ${r.data.inregistrariActualizate} înregistrări actualizate`);
      loadTab(tab);
    } catch (e) {
      toast('Eroare: ' + (e.response?.data?.error || e.message), 'error');
    }
  };

  const fEditable = (field, label, opts) => (
    <div className="form-group">
      <label>{label}{campuriScanate.has(field) && <span style={{color:'#2d6ca2',fontWeight:'normal'}}> 📷 din bon</span>}</label>
      <input list={`dl-${field}`} type="text" value={form[field] || ''}
        onChange={e => { setForm({ ...form, [field]: e.target.value }); campuriScanate.delete(field); setCampuriScanate(new Set(campuriScanate)); }}
        placeholder="Alege sau scrie..."
        style={campuriScanate.has(field) ? { borderColor:'#2d6ca2', background:'#eef5fc' } : undefined} />
      <datalist id={`dl-${field}`}>{(opts || []).map(o => <option key={o} value={o} />)}</datalist>
    </div>
  );

  const f = (field, label, type = 'text', opts = null, onChangeCb = null, required = false) => (
    <div className="form-group">
      <label>{label}{required && <span style={{color:'#e74c3c'}}> *</span>}{campuriScanate.has(field) && <span style={{color:'#2d6ca2',fontWeight:'normal'}}> 📷 din bon</span>}</label>
      {opts
        ? <select value={form[field] || ''} onChange={e => { setForm({ ...form, [field]: e.target.value }); campuriScanate.delete(field); setCampuriScanate(new Set(campuriScanate)); onChangeCb && onChangeCb(e.target.value); }}
            style={campuriScanate.has(field) ? { borderColor:'#2d6ca2', background:'#eef5fc' } : undefined}>
            <option value="">--</option>{opts.map(o => <option key={o}>{o}</option>)}
          </select>
        : <input type={type} value={form[field] || ''} onChange={e => { setForm({ ...form, [field]: e.target.value }); campuriScanate.delete(field); setCampuriScanate(new Set(campuriScanate)); onChangeCb && onChangeCb(e.target.value); }}
            style={campuriScanate.has(field) ? { borderColor:'#2d6ca2', background:'#eef5fc' } : undefined} />
      }
    </div>
  );

  const SH = ({ col, children }) => (
    <th onClick={() => toggleSort(col)} style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>
      {children}{sortIcon(col)}
    </th>
  );

  const TD = ({ val }) => (
    <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmt(val)}</td>
  );

  return (
    <div>
      <div className="tabs">
        <button className={`tab-btn ${tab === 'facturi' ? 'active' : ''}`} onClick={() => setTab('facturi')}>Facturi intrare</button>
        <button className={`tab-btn ${tab === 'consum' ? 'active' : ''}`} onClick={() => setTab('consum')}>Consum / Alimentări</button>
        <button className={`tab-btn ${tab === 'sold' ? 'active' : ''}`} onClick={() => setTab('sold')}>Sold stoc</button>
      </div>

      {tab !== 'sold' && (
        <>
          <div className="date-filter-bar">
            <label>Perioadă:</label>
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}/>
            <span className="sep">—</span>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}/>
            {(dateFrom || dateTo) && <button className="btn btn-sm" style={{ background: '#eee' }} onClick={() => { setDateFrom(''); setDateTo(''); }}>✕</button>}
          </div>

          {/* Bara totaluri + actiuni */}
          <div style={{ background: '#e8eef5', padding: '8px 14px', borderBottom: '1px solid #d0dcea', display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12, color: '#555' }}>{filtered.length} înregistrări{Object.values(filters).some(v => v) || dateFrom || dateTo ? ' (filtrate)' : ''}</span>
            <span style={{ fontWeight: 'bold', color: '#1a3a5c', fontSize: 13 }}>
              Total cantitate: {fmt(totalCantitate)} {unitatePentru(filters.tipCombustibil)}
              {tab === 'facturi' && <> &nbsp;|&nbsp; Total valoare: {fmt(totalValoare)} RON</>}
              {tab === 'consum' && (
                <>
                  &nbsp;|&nbsp; Consum mediu: {consumMediu !== null ? `${fmt(consumMediu)} ${unitatePentru(filters.tipCombustibil)}/100km` : (
                    filters.nrInmatriculare ? '— (min. 2 alimentări)' : '— (selectează o mașină)'
                  )}
                </>
              )}
            </span>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
              <button className="btn btn-sm" style={{ background: '#217346', color: 'white' }}
                onClick={() => exportCSV(sorted, `carburant_${tab}`)}>📊 CSV</button>
              <button className="btn btn-sm" style={{ background: '#555', color: 'white' }}
                onClick={() => printTable(`Carburant — ${tab}`, sorted)}>🖨️ Print</button>
              <button className="btn btn-success btn-sm" onClick={openAdd}>+ Adaugă</button>
            </div>
          </div>
        </>
      )}

      {loading ? <Spinner /> : <>
        {tab === 'facturi' && (
          <div style={{ overflowX: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <SH col="furnizor">Furnizor</SH>
                  <SH col="nrFactura">Nr. Factură</SH>
                  <SH col="dataFactura">Data</SH>
                  <SH col="tipCombustibil">Combustibil</SH>
                  <SH col="cantitate">Cantitate</SH>
                  <th style={{ textAlign: 'right' }}>Preț/l</th>
                  <th style={{ textAlign: 'right' }}>Total</th>
                  <SH col="nrInmatriculare">Nr. Auto</SH>
                  <SH col="kmLaAlimentare">KM bord</SH>
                  <th>Bon</th>
                  <th>Acțiuni</th>
                </tr>
                <tr style={{ background: '#f0f4fa' }}>
                  <th>
                    <select value={filters.furnizor || ''} onChange={e => setFilter('furnizor', e.target.value)}
                      style={{width:'100%',padding:'3px 6px',fontSize:11,border:'1px solid #ccc',borderRadius:3}}>
                      <option value=''>Toți</option>
                      {furnizori.map(f => <option key={f} value={f}>{f}</option>)}
                    </select>
                  </th>
                  <th><FilterInput value={filters.nrFactura || ''} onChange={v => setFilter('nrFactura', v)} /></th>
                  <th><FilterInput value={filters.dataFactura || ''} onChange={v => setFilter('dataFactura', v)} /></th>
                  <th>
                    <select value={filters.tipCombustibil || ''} onChange={e => setFilter('tipCombustibil', e.target.value)}
                      style={{width:'100%',padding:'3px 6px',fontSize:11,border:'1px solid #ccc',borderRadius:3}}>
                      <option value=''>Toate</option>
                      {TIPURI.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </th>
                  <th></th><th></th><th></th>
                  <th>
                    <select value={filters.nrInmatriculare || ''} onChange={e => setFilter('nrInmatriculare', e.target.value)}
                      style={{width:'100%',padding:'3px 6px',fontSize:11,border:'1px solid #ccc',borderRadius:3}}>
                      <option value=''>Toate</option>
                      {nruri.map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </th>
                  <th></th><th></th><th></th>
                </tr>
              </thead>
              <tbody>
                {sorted.map(r => {
                  const isSel = selectedRow?.nrCrt === r.nrCrt;
                  return <tr key={r.nrCrt}
                    onClick={()=>setSelectedRow(isSel?null:r)}
                    style={{cursor:'pointer',background:isSel?'#e8f0fe':undefined}}>
                    <td>{r.furnizor}</td>
                    <td>{r.nrFactura}</td>
                    <td>{r.dataFactura}</td>
                    <td>{r.tipCombustibil}</td>
                    <TD val={r.cantitate} />
                    <TD val={r.pretUnitar} />
                    <TD val={parseFloat(r.cantitate || 0) * parseFloat(r.pretUnitar || 0)} />
                    <td>{r.nrInmatriculare}</td>
                    <td style={{ textAlign: 'right' }}>{fmtKm(r.kmLaAlimentare)}</td>
                    <td style={{ textAlign: 'center' }}>{r.bonFiscal ? '🧾' : ''}</td>
                    <td onClick={e=>e.stopPropagation()} style={{whiteSpace:'nowrap'}}>
                      {!isDriver && (<>
                        <button className="btn btn-sm" style={{marginRight:4}} onClick={()=>openEdit(r)}>✏️</button>
                        <button className="btn btn-sm btn-danger" onClick={()=>del(r)}>🗑️</button>
                      </>)}
                    </td>
                  </tr>;
                })}
                {sorted.length === 0 && <tr><td colSpan={10} style={{ textAlign: 'center', padding: 20, color: '#888' }}>Fără date</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {tab === 'consum' && (
          <div style={{ overflowX: 'auto' }}>
            <div style={{ padding: '8px 12px', display:'flex', justifyContent:'flex-end' }}>
              <button className="btn btn-sm" style={{background:'#eee'}}
                onClick={()=>recalculeaza(selectedRow?.nrInmatriculare)}
                title="Recalculează tot istoricul de consum pentru vehiculul selectat (click pe un rând)">
                🔄 Recalculează istoric {selectedRow ? `(${selectedRow.nrInmatriculare})` : ''}
              </button>
            </div>
            <table>
              <thead>
                <tr>
                  <SH col="nrInmatriculare">Nr. Auto</SH>
                  <SH col="tipCombustibil">Combustibil</SH>
                  <SH col="cantitate">Cantitate</SH>
                  <SH col="kmLaAlimentare">KM bord</SH>
                  <SH col="dataAlimentarii">Data</SH>
                  <SH col="consumCalculat">Consum</SH>
                  <th>Acțiuni</th>
                </tr>
                <tr style={{ background: '#f0f4fa' }}>
                  <th>
                    <select value={filters.nrInmatriculare || ''} onChange={e => setFilter('nrInmatriculare', e.target.value)}
                      style={{width:'100%',padding:'3px 6px',fontSize:11,border:'1px solid #ccc',borderRadius:3}}>
                      <option value=''>Toate</option>
                      {nruri.map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </th>
                  <th><FilterInput value={filters.tipCombustibil || ''} onChange={v => setFilter('tipCombustibil', v)} /></th>
                  <th></th>
                  <th><FilterInput value={filters.kmLaAlimentare || ''} onChange={v => setFilter('kmLaAlimentare', v)} /></th>
                  <th><FilterInput value={filters.dataAlimentarii || ''} onChange={v => setFilter('dataAlimentarii', v)} /></th>
                  <th></th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {sorted.map(r => {
                  const isSel2 = selectedRow?.nrCrtCombust === r.nrCrtCombust;
                  const esteEvidentiat = randConsumEvidentiat === r.nrCrtCombust;
                  return <tr key={r.nrCrtCombust}
                    onClick={()=>{setSelectedRow(isSel2?null:r); if(esteEvidentiat) setRandConsumEvidentiat(null);}}
                    style={{
                      cursor:'pointer',
                      background: esteEvidentiat ? '#fff3cd' : (isSel2?'#e8f0fe':undefined),
                      outline: esteEvidentiat ? '2px solid #f0c14b' : undefined,
                    }}>
                    <td><b>{r.nrInmatriculare}</b>{esteEvidentiat && <span style={{marginLeft:6,fontSize:11,color:'#8a6d00'}} title="Legată de o factură modificată recent">⚠️ verifică</span>}</td>
                    <td>{r.tipCombustibil}</td>
                    <TD val={r.cantitate} />
                    <td style={{ textAlign: 'right' }}>{fmtKm(r.kmLaAlimentare)}</td>
                    <td>{r.dataAlimentarii}</td>
                    <td style={{ textAlign: 'right', fontWeight: 'bold' }}>
                      {r.consumCalculat != null ? fmt(r.consumCalculat) : '—'}
                    </td>
                    <td onClick={e=>e.stopPropagation()} style={{whiteSpace:'nowrap'}}>
                      {!isDriver && (<>
                        <button className="btn btn-sm" style={{marginRight:4}} onClick={()=>openEdit(r)}>✏️</button>
                        <button className="btn btn-sm btn-danger" onClick={()=>del(r)}>🗑️</button>
                      </>)}
                    </td>
                  </tr>;
                })}
                {sorted.length === 0 && <tr><td colSpan={7} style={{ textAlign: 'center', padding: 20, color: '#888' }}>Fără date</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {tab === 'sold' && (
          <div style={{ padding: 16 }}>
            <table>
              <thead>
                <tr>
                  <th>Tip combustibil</th>
                  <th style={{ textAlign: 'right' }}>Achiziționat (l)</th>
                  <th style={{ textAlign: 'right' }}>Consumat (l)</th>
                  <th style={{ textAlign: 'right' }}>Rest stoc (l)</th>
                </tr>
              </thead>
              <tbody>
                {sold.map(r => (
                  <tr key={r.tipCombustibil}>
                    <td><b>{r.tipCombustibil}</b></td>
                    <td style={{ textAlign: 'right' }}>{fmt(r.achizitionat)}</td>
                    <td style={{ textAlign: 'right' }}>{fmt(r.consumat)}</td>
                    <td style={{ textAlign: 'right', fontWeight: 'bold', color: parseFloat(r.rest || 0) < 0 ? 'red' : 'green' }}>
                      {fmt(r.rest)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </>}

      {/* MODAL FACTURI cu Bon Fiscal */}
      {modal && tab === 'facturi' && (
        <Modal title={modal.mode === 'add' ? 'Adaugă factură combustibil' : 'Editează factură'} onClose={() => setModal(null)} onSave={save}>
          {modal.mode === 'edit' && form.consumCarburantId && (
            <div style={{ background: '#fff8e6', border: '2px solid #f0c14b', borderRadius: 6, padding: '10px 14px', marginBottom: 14 }}>
              <div style={{ fontWeight: 'bold', color: '#8a6d00', marginBottom: 4 }}>
                ⚠️ Acest bon e legat de o alimentare din Consum/Alimentări
              </div>
              <div style={{ fontSize: 12.5, color: '#6b5500' }}>
                Modificarea de aici nu actualizează automat înregistrarea din Consum/Alimentări — după ce salvezi,
                mergi acolo și verifică/corectează manual rândul respectiv (îl vei vedea evidențiat).
              </div>
            </div>
          )}
          {modal.mode === 'add' && (
            <div className="form-row single" style={{ marginBottom: 10 }}>
              <input ref={fisierScanRef} type="file" accept="image/*" capture="environment"
                style={{ display: 'none' }} onChange={scaneazaBon} />
              <button type="button" className="btn btn-sm" style={{ background: '#2d6ca2', color: 'white' }}
                onClick={deschideScanare} disabled={scanand}>
                {scanand ? `📷 Se citește bonul... ${progresScan}%` : '📷 Scanează bonul (cameră)'}
              </button>
              <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>
                Fă o poză clară bonului fiscal - completăm automat furnizorul, nr. bon, data, tipul de
                combustibil, cantitatea și prețul/litru. Verifică datele înainte de a salva.
              </div>
            </div>
          )}

          {verificareDuplicat.stare === 'verifica' && (
            <div style={{ fontSize: 12, color: '#888', marginBottom: 10 }}>🔎 Se verifică dacă bonul a mai fost înregistrat...</div>
          )}
          {verificareDuplicat.stare === 'duplicat' && (
            <div style={{ background: '#fde8e8', border: '2px solid #e74c3c', borderRadius: 6, padding: '10px 14px', marginBottom: 14 }}>
              <div style={{ fontWeight: 'bold', color: '#c0392b', marginBottom: 6 }}>
                ⚠️ Acest bon pare deja înregistrat!
              </div>
              {verificareDuplicat.rezultate.map(r => (
                <div key={r.nrCrt} style={{ fontSize: 12.5, color: '#721c24' }}>
                  {r.furnizor} — factura {r.nrFactura} din {r.dataFactura} — {r.tipCombustibil}, {r.cantitate} l × {r.pretUnitar} lei
                  {r.nrInmatriculare ? ` — auto ${r.nrInmatriculare}` : ' — stoc'}
                  {r.operare ? ` — introdus de ${r.operare}` : ''}
                </div>
              ))}
              <div style={{ fontSize: 11.5, color: '#721c24', marginTop: 4 }}>
                Verifică — dacă e într-adevăr același bon, nu-l mai salva o dată.
              </div>
            </div>
          )}
          {verificareDuplicat.stare === 'ok' && (
            <div style={{ fontSize: 12, color: '#1a7a3a', marginBottom: 10 }}>✅ Bon nou, nu a mai fost înregistrat.</div>
          )}

          {/* Bon fiscal checkbox - prominent */}
          <div style={{ background: form.bonFiscal ? '#e8f8ee' : '#f7f9fc', border: '2px solid ' + (form.bonFiscal ? '#27ae60' : '#ccc'), borderRadius: 6, padding: '10px 14px', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 10 }}>
            <input type="checkbox" id="bonFiscal" checked={!!form.bonFiscal}
              onChange={e => setForm({ ...form, bonFiscal: e.target.checked })}
              style={{ width: 18, height: 18, cursor: 'pointer' }} />
            <label htmlFor="bonFiscal" style={{ fontWeight: 'bold', fontSize: 13, cursor: 'pointer', color: form.bonFiscal ? '#1a7a3a' : '#555' }}>
              🧾 Bon fiscal — înregistrează automat și în Consum/Alimentări
            </label>
          </div>

          <div className="form-row">{fEditable('furnizor', 'Furnizor', furnizori)}{f('nrFactura', 'Nr. Factură')}</div>
          <div className="form-row">{f('dataFactura', 'Data factură', 'date')}{f('tipCombustibil', 'Tip combustibil', 'text', TIPURI, true)}</div>
          <div className="form-row">{f('cantitate', `Cantitate (${unitatePentru(form.tipCombustibil)})`, 'number')}{f('pretUnitar', `Preț/${unitatePentru(form.tipCombustibil)==='kWh'?'kWh':'litru'}`, 'number')}</div>

          {form.cantitate && form.pretUnitar && (
            <div style={{ background: '#e8f8ee', padding: '6px 10px', borderRadius: 4, marginBottom: 10, fontSize: 12 }}>
              Valoare factură: <b>{fmt(parseFloat(form.cantitate) * parseFloat(form.pretUnitar))} RON</b>
            </div>
          )}

          {/* Campuri pentru bon fiscal */}
          {form.bonFiscal && (
            <>
              <div style={{ borderTop: '1px dashed #ccc', margin: '10px 0', paddingTop: 10 }}>
                <div style={{ fontSize: 11, fontWeight: 'bold', color: '#27ae60', marginBottom: 8 }}>Date consum (din bon fiscal):</div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Nr. Auto alimentat<span style={{color:'#e74c3c'}}> *</span></label>
                    <select value={form.nrInmatriculare || ''} onChange={e => { setForm({ ...form, nrInmatriculare: e.target.value }); onAutoSelect(e.target.value); }}>
                      <option value="">--</option>
                      {nruri.map(o => <option key={o}>{o}</option>)}
                    </select>
                  </div>
                  {f('kmLaAlimentare', 'KM la bord', 'number', null, null, true)}
                </div>
                {soldInfo && (
                  <div style={{ background: soldInfo.sold <= 0 ? '#fde8e8' : '#e8f8ee', border: '1px solid #ccc', borderRadius: 6, padding: '6px 12px', marginBottom: 8, fontSize: 12 }}>
                    Combustibil: <b>{combustibilAuto}</b> — Sold curent: <b>{fmt(soldInfo.sold)} {unitatePentru(combustibilAuto)}</b>
                  </div>
                )}
              </div>
            </>
          )}

          {!form.bonFiscal && (
            <div className="form-row single">
              {f('nrInmatriculare', 'Nr. Auto (opțional)', 'text', nruri)}
            </div>
          )}
          <div className="form-row single"><span style={{fontSize:11,color:'#888'}}>* Câmp obligatoriu</span></div>
        </Modal>
      )}

      {/* MODAL CONSUM */}
      {modal && tab === 'consum' && (
        <Modal title={modal.mode === 'add' ? 'Adaugă alimentare' : 'Editează alimentare'} onClose={() => setModal(null)} onSave={save}>
          <div className="form-row single">
            <div className="form-group">
              <label>Nr. Auto<span style={{color:'#e74c3c'}}> *</span></label>
              <select value={form.nrInmatriculare || ''} onChange={e => { setForm({ ...form, nrInmatriculare: e.target.value }); onAutoSelect(e.target.value); }}>
                <option value="">--</option>
                {nruri.map(o => <option key={o}>{o}</option>)}
              </select>
            </div>
          </div>
          {soldInfo && (
            <div style={{ background: soldInfo.sold <= 0 ? '#fde8e8' : soldInfo.sold < 50 ? '#fff3cd' : '#e8f8ee', border: '1px solid #ccc', borderRadius: 6, padding: '8px 12px', marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: '#666' }}>Combustibil: <b>{combustibilAuto}</b></div>
              <div style={{ fontSize: 13, fontWeight: 'bold', color: soldInfo.sold <= 0 ? '#c0392b' : '#1a7a3a' }}>
                Sold disponibil: {fmt(soldInfo.sold)} {unitatePentru(combustibilAuto)}
              </div>
              {soldInfo.sold <= 0 && <div style={{ fontSize: 11, color: '#c0392b' }}>⚠️ Stoc epuizat!</div>}
            </div>
          )}
          <div className="form-row">{f('cantitate', `Cantitate (${unitatePentru(combustibilAuto)})`, 'number')}{f('kmLaAlimentare', 'KM la bord', 'number', null, null, true)}</div>
          <div className="form-row single">{f('dataAlimentarii', 'Data alimentare', 'date')}</div>
          {soldInfo && form.cantitate && parseFloat(form.cantitate) > soldInfo.sold && (
            <div style={{ background: '#fde8e8', padding: '6px 10px', borderRadius: 4, fontSize: 12, color: '#c0392b' }}>
              ⚠️ Cantitatea ({form.cantitate} {unitatePentru(combustibilAuto)}) depășește soldul ({fmt(soldInfo.sold)} {unitatePentru(combustibilAuto)})!
            </div>
          )}
          <div className="form-row single"><span style={{fontSize:11,color:'#888'}}>* Câmp obligatoriu</span></div>
        </Modal>
      )}
    </div>
  );
}
